<template>
  <BForm @submit.prevent>
    <BRow>
      <BCol md="12">
        <BaseInput
          v-model="localOrganizationName"
          label="自治体組織名"
          type="text"
          placeholder="例: 廃棄物管理課"
          :maxlength="validationRules.ORGANIZATION_NAME_MAX_LENGTH"
          :required="true"
          :error-message="errors.organizationName || validationError"
          :disabled="loading"
        />
      </BCol>
    </BRow>

    <!-- 文字数カウンター -->
    <div class="d-flex justify-content-between align-items-center mt-2">
      <div class="text-muted small">
        <i class="bi bi-info-circle me-1"></i>
        * の項目は必須入力です。
      </div>
      <div class="text-muted small">
        {{ currentLength }}/{{ validationRules.ORGANIZATION_NAME_MAX_LENGTH }}文字
      </div>
    </div>

    <!-- リアルタイムバリデーション結果表示 -->
    <div v-if="validationError" class="text-danger small mt-1">
      <i class="bi bi-exclamation-triangle me-1"></i>
      {{ validationError }}
    </div>
  </BForm>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { BForm, BRow, BCol } from 'bootstrap-vue-next'
import BaseInput from '@/components/atoms/BaseInput.vue'
import * as municipalOrganizationService from '@/services/systemSettings/municipalOrganizationService.js'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  },
  existingOrganizations: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

const validationRules = municipalOrganizationService.getValidationRules()

// ローカルstate（BaseInputはv-modelで繋ぎ、カーソルリセットを防ぐ）
const localOrganizationName = ref(props.modelValue.organizationName ?? '')

// 親からmodelValueが変わったときにローカルを同期
watch(() => props.modelValue.organizationName, (newVal) => {
  if (newVal !== localOrganizationName.value) {
    localOrganizationName.value = newVal ?? ''
  }
})

// ローカルの変更を親に伝える
watch(localOrganizationName, (newVal) => {
  emit('update:modelValue', { ...props.modelValue, organizationName: newVal })
})

const currentLength = computed(() => {
  return localOrganizationName.value ? localOrganizationName.value.length : 0
})

const validationError = computed(() => {
  if (!localOrganizationName.value) {
    return null
  }

  // 基本バリデーション
  const basicError = municipalOrganizationService.validateOrganizationName(localOrganizationName.value)
  if (basicError) {
    return basicError
  }

  // 重複チェック（編集時は自分自身を除外）
  const excludeId = props.modelValue.id || null
  if (municipalOrganizationService.isDuplicateOrganizationName(
    localOrganizationName.value,
    props.existingOrganizations,
    excludeId
  )) {
    return 'この組織名は既に登録されています'
  }

  return null
})

const isValid = computed(() => {
  return !validationError.value && localOrganizationName.value?.trim()
})

/**
 * フォームの妥当性を外部から確認するメソッド
 */
const validate = () => {
  return isValid.value
}

/**
 * 現在のバリデーションエラーを取得
 */
const getValidationError = () => {
  return validationError.value
}

// 外部から呼び出せるメソッドを公開
defineExpose({
  validate,
  getValidationError
})
</script>

<style scoped>
/* BaseInputが統一されたスタイリングを提供するため、カスタムスタイルは最小限に */
</style>
